// Channel-stream decompressors for PSD/PSB.
//
// PSD encodes per-channel pixel data with one of four compression schemes
// (see Adobe spec, "Channel image data"):
//
//   0  raw                 — bytes verbatim
//   1  PackBits / RLE      — byte-oriented run-length, Apple's spec
//   2  ZIP without prediction      — raw Deflate / zlib stream
//   3  ZIP with prediction         — Deflate, then row-wise delta-decode
//
// These functions take a payload `Uint8Array` (already sliced past the
// 2-byte compression code) and produce the raw channel plane.  Decoded data
// for one channel is `width * height * (depth/8)` bytes.
//
// Implementation notes:
//   - PackBits is decoded into an exact-size output buffer; mismatched
//     lengths throw, because they almost always mean the upstream
//     scanline-count table is wrong.
//   - ZIP uses Node's `zlib` module for deflate/zlib decompression.
//     Calling code must `await`.
//   - Prediction undo is depth-aware.  For 8-bit channels prediction is
//     byte-wise along scanlines.  16-bit is byte-pair-wise (high byte first,
//     then low byte) per the spec.  32-bit prediction reorders bytes by
//     significance across the whole row before delta-decoding; this matches
//     Photoshop and ImageMagick.

import zlib from "node:zlib";

const TEXT_DEC = new TextDecoder();

/** Identity decoder — kept for symmetry with the 4-way switch in callers. */
export function decodeRaw(src: Uint8Array): Uint8Array {
  // Return a fresh copy so callers can mutate / hand off without surprises.
  return new Uint8Array(src);
}

/**
 * Apple PackBits decoder.
 *
 * The control byte `n` is signed:
 *   n in [0, 127]    → copy the next n+1 bytes verbatim
 *   n in [-127, -1]  → repeat the next byte (1-n) times
 *   n == -128        → no-op
 *
 * `expectedBytes` is the decoded byte count this stream should produce.
 * Mismatch is fatal (almost always a wrong scanline-table read).
 */
export function decodePackBits(
  src: Uint8Array,
  expectedBytes: number,
): Uint8Array {
  const out = new Uint8Array(expectedBytes);
  let si = 0;
  let di = 0;
  while (si < src.length) {
    // Read as signed byte.
    const raw = src[si]!;
    const n = raw > 127 ? raw - 256 : raw;
    si += 1;
    if (n === -128) {
      // No-op per spec.
      continue;
    }
    if (n >= 0) {
      const count = n + 1;
      if (si + count > src.length) {
        throw new Error(
          `PackBits literal run reads past end of input (need ${count} ` +
            `bytes at si=${si}, have ${src.length - si})`,
        );
      }
      if (di + count > out.length) {
        throw new Error(
          `PackBits output overflow: expected ${expectedBytes}, ` +
            `would write to ${di + count}`,
        );
      }
      out.set(src.subarray(si, si + count), di);
      si += count;
      di += count;
    } else {
      const count = 1 - n; // 2..128
      if (si + 1 > src.length) {
        throw new Error(
          `PackBits run byte missing at si=${si} (input length ${src.length})`,
        );
      }
      const value = src[si]!;
      si += 1;
      if (di + count > out.length) {
        throw new Error(
          `PackBits output overflow: expected ${expectedBytes}, ` +
            `would write to ${di + count}`,
        );
      }
      out.fill(value, di, di + count);
      di += count;
    }
  }
  if (di !== expectedBytes) {
    throw new Error(
      `PackBits decode size mismatch: expected ${expectedBytes} bytes, ` +
        `produced ${di}`,
    );
  }
  return out;
}

/**
 * ZIP/Deflate decoder built on the host `DecompressionStream`.
 *
 * PSD's "ZIP" is in practice a zlib stream (RFC 1950, with the 2-byte
 * header).  Photoshop has occasionally been observed writing raw Deflate
 * (RFC 1951) for these channels, so we try `deflate` first (zlib-wrapped),
 * then fall back to `deflate-raw`.  Either way the output is the
 * concatenated channel plane.
 */
export async function decodeZip(src: Uint8Array): Promise<Uint8Array> {
  // Heuristic: zlib streams start with 0x78 followed by a flag byte whose
  // value combined with 0x78 is divisible by 31.  Anything else, try raw
  // deflate first.  This avoids the costly throw/retry in the common case.
  const looksLikeZlib =
    src.length >= 2 &&
    src[0] === 0x78 &&
    ((src[0]! << 8) | src[1]!) % 31 === 0;
  const order: ("deflate" | "deflate-raw")[] = looksLikeZlib
    ? ["deflate", "deflate-raw"]
    : ["deflate-raw", "deflate"];
  let lastErr: unknown = null;
  for (const format of order) {
    try {
      return await decompressWith(src, format);
    } catch (err) {
      lastErr = err;
    }
  }
  throw new Error(
    `ZIP decode failed in both "deflate" and "deflate-raw" modes: ${
      lastErr instanceof Error ? lastErr.message : String(lastErr)
    }`,
  );
}

async function decompressWith(
  src: Uint8Array,
  format: "deflate" | "deflate-raw",
): Promise<Uint8Array> {
  // Make a fresh ArrayBuffer copy so we own the bytes (subarrays can share
  // memory with the source PSD buffer, which the stream may not like).
  const copy = new Uint8Array(src.length);
  copy.set(src);
  if (format === "deflate") {
    return zlib.inflateSync(copy);
  }
  return zlib.inflateRawSync(copy);
}

/**
 * ZIP-with-prediction.  Decompresses the Deflate stream, then walks each
 * row undoing the delta predictor.  For 8-bit channels the predictor is
 * applied per byte along the scanline.  For 16-bit the predictor is applied
 * to 16-bit big-endian samples (which Photoshop stores as separate high /
 * low byte planes within each row).  For 32-bit channels Photoshop
 * additionally byte-shuffles each row by significance — i.e. the row is
 * `width` MSBs, then `width` next-bytes, then `width` next, then `width`
 * LSBs — before prediction is applied at the byte level.  We undo the
 * prediction first and then un-shuffle.
 */
export async function decodeZipPrediction(
  src: Uint8Array,
  width: number,
  height: number,
  depth: 8 | 16 | 32,
): Promise<Uint8Array> {
  const raw = await decodeZip(src);
  const bytesPerSample = depth / 8;
  const expected = width * height * bytesPerSample;
  if (raw.length !== expected) {
    throw new Error(
      `ZIP-prediction: decompressed size ${raw.length} != expected ${expected} ` +
        `(width=${width}, height=${height}, depth=${depth})`,
    );
  }

  if (depth === 8) {
    // Apply byte-wise delta along each scanline.
    for (let y = 0; y < height; y++) {
      const base = y * width;
      for (let x = 1; x < width; x++) {
        raw[base + x] = (raw[base + x]! + raw[base + x - 1]!) & 0xff;
      }
    }
    return raw;
  }

  if (depth === 16) {
    // 16-bit samples, big-endian, with prediction applied to the sample (not
    // the byte).  Photoshop stores `width` u16 values per row in BE order;
    // each row contributes `width * 2` bytes.
    const rowBytes = width * 2;
    for (let y = 0; y < height; y++) {
      const base = y * rowBytes;
      let prev =
        (raw[base]! << 8) | raw[base + 1]!;
      for (let x = 1; x < width; x++) {
        const off = base + x * 2;
        const delta = (raw[off]! << 8) | raw[off + 1]!;
        const sample = (prev + delta) & 0xffff;
        raw[off] = (sample >>> 8) & 0xff;
        raw[off + 1] = sample & 0xff;
        prev = sample;
      }
    }
    return raw;
  }

  // depth === 32: byte-shuffled, byte-delta-predicted rows.
  const rowBytes = width * 4;
  const tmp = new Uint8Array(rowBytes);
  for (let y = 0; y < height; y++) {
    const base = y * rowBytes;
    // Step 1: byte-wise delta along the row (still in shuffled order).
    for (let i = 1; i < rowBytes; i++) {
      raw[base + i] = (raw[base + i]! + raw[base + i - 1]!) & 0xff;
    }
    // Step 2: un-shuffle into MSB-first interleaved samples.  Source row is
    //   [b0_0, b0_1, …, b0_w-1, b1_0, …, b1_w-1, b2_0, …, b3_w-1].
    for (let x = 0; x < width; x++) {
      tmp[x * 4 + 0] = raw[base + 0 * width + x]!;
      tmp[x * 4 + 1] = raw[base + 1 * width + x]!;
      tmp[x * 4 + 2] = raw[base + 2 * width + x]!;
      tmp[x * 4 + 3] = raw[base + 3 * width + x]!;
    }
    raw.set(tmp, base);
  }
  return raw;
}

// Suppress an "unused import" lint warning for the TextDecoder constant; it
// is kept available as a tiny utility for future callers that may want to
// surface decoded error context.
void TEXT_DEC;
